// Browser-side voice recorder. Two modes:
//   - streaming: opens a WebSocket to /voice/stream, pumps MediaRecorder chunks
//     to the server, surfaces interim transcripts as they arrive
//   - batch: records to local Blob, uploads to /voice/sessions/batch on stop
//
// Streaming is the default; callers fall back to batch when WebSocket fails.

import { api, buildWebSocketUrl } from '../api';

export type RecorderMode = 'streaming' | 'batch';

export type TranscriptEvent =
  | { type: 'open'; sessionId: string }
  | { type: 'interim'; text: string; isFinal: boolean; confidence: number }
  | { type: 'utteranceEnd' }
  | {
      type: 'final';
      sessionId: string;
      transcript: string;
      confidence: number;
      durationMs: number;
    }
  | { type: 'error'; message: string };

export interface RecorderOptions {
  mode?: RecorderMode;
  purpose?: 'quote' | 'lookup' | 'note';
  onEvent: (event: TranscriptEvent) => void;
  /** Receives raw audio amplitude (0..1) for visualizer rendering, ~50hz. */
  onLevel?: (level: number) => void;
}

export interface FinalResult {
  sessionId: string;
  transcript: string;
  confidence: number;
  durationMs: number;
}

const STREAMING_URL = '/voice/stream';
const BATCH_URL = '/voice/sessions/batch';

export class VoiceRecorder {
  private stream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private audioContext: AudioContext | null = null;
  private levelInterval: number | null = null;
  private ws: WebSocket | null = null;
  private chunks: Blob[] = [];
  private mode: RecorderMode;
  private mimeType = 'audio/webm';
  private finalResolver?: (result: FinalResult) => void;
  private finalRejecter?: (err: Error) => void;

  constructor(private readonly opts: RecorderOptions) {
    this.mode = opts.mode ?? 'streaming';
  }

  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    this.startVisualizer(this.stream);

    this.mimeType = pickMimeType();
    this.mediaRecorder = new MediaRecorder(this.stream, {
      mimeType: this.mimeType,
      audioBitsPerSecond: 24000,
    });

    if (this.mode === 'streaming') {
      await this.connectWebSocket();
      this.mediaRecorder.ondataavailable = async (e) => {
        if (e.data.size === 0 || this.ws?.readyState !== WebSocket.OPEN) return;
        this.chunks.push(e.data);
        const buf = await e.data.arrayBuffer();
        this.ws.send(buf);
      };
      // Send one chunk per 250ms — small enough for low latency, big enough
      // to avoid burst write overhead on the WS.
      this.mediaRecorder.start(250);
    } else {
      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) this.chunks.push(e.data);
      };
      this.mediaRecorder.start();
    }
  }

  async stop(): Promise<FinalResult> {
    if (!this.mediaRecorder) throw new Error('recorder not started');
    return new Promise<FinalResult>((resolve, reject) => {
      this.finalResolver = resolve;
      this.finalRejecter = reject;

      if (this.mode === 'streaming') {
        this.mediaRecorder?.stop();
        // Tell the server we're done; it returns the final transcript over the
        // same WebSocket and closes.
        try {
          this.ws?.send(JSON.stringify({ type: 'close' }));
        } catch {
          this.cleanup();
          reject(new Error('failed to send close message'));
        }
      } else {
        const recorder = this.mediaRecorder;
        if (!recorder) {
          reject(new Error('recorder not initialized'));
          return;
        }
        recorder.onstop = async () => {
          try {
            const blob = new Blob(this.chunks, { type: this.mimeType });
            const result = await uploadBatch(blob, this.opts.purpose ?? 'quote');
            resolve(result);
          } catch (err) {
            reject(err as Error);
          } finally {
            this.cleanup();
          }
        };
        recorder.stop();
      }
    });
  }

  cancel(): void {
    if (this.finalRejecter) {
      this.finalRejecter(new Error('recording cancelled'));
      this.finalRejecter = undefined;
      this.finalResolver = undefined;
    }
    this.cleanup();
  }

  private async connectWebSocket(): Promise<void> {
    const url = buildWebSocketUrl(STREAMING_URL);
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(url);
      ws.binaryType = 'arraybuffer';
      this.ws = ws;
      const onOpen = () => {
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
        resolve();
      };
      const onError = () => {
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
        reject(new Error('websocket failed to open'));
      };
      ws.addEventListener('open', onOpen);
      ws.addEventListener('error', onError);
      ws.addEventListener('message', (event) => {
        try {
          const msg = JSON.parse(event.data as string) as TranscriptEvent;
          this.opts.onEvent(msg);
          if (msg.type === 'final' && this.finalResolver) {
            this.finalResolver({
              sessionId: msg.sessionId,
              transcript: msg.transcript,
              confidence: msg.confidence,
              durationMs: msg.durationMs,
            });
            this.finalResolver = undefined;
            this.finalRejecter = undefined;
            this.cleanup();
          }
        } catch {
          // ignore non-JSON messages
        }
      });
      ws.addEventListener('close', () => {
        if (this.finalRejecter) {
          this.finalRejecter(new Error('websocket closed before final transcript'));
          this.finalRejecter = undefined;
          this.finalResolver = undefined;
        }
      });
    });
  }

  private startVisualizer(stream: MediaStream): void {
    if (!this.opts.onLevel) return;
    try {
      const ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      this.audioContext = ctx;
      const buf = new Uint8Array(analyser.fftSize);
      this.levelInterval = window.setInterval(() => {
        analyser.getByteTimeDomainData(buf);
        // Compute peak amplitude relative to silence (128).
        let peak = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = Math.abs((buf[i] as number) - 128) / 128;
          if (v > peak) peak = v;
        }
        this.opts.onLevel?.(peak);
      }, 50);
    } catch {
      // Visualizer is best-effort — never break recording over it.
    }
  }

  private cleanup(): void {
    if (this.levelInterval !== null) {
      clearInterval(this.levelInterval);
      this.levelInterval = null;
    }
    this.audioContext?.close().catch(() => undefined);
    this.audioContext = null;
    if (this.stream) {
      for (const t of this.stream.getTracks()) t.stop();
    }
    this.stream = null;
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
      try {
        this.ws.close();
      } catch {
        // ignore
      }
    }
    this.ws = null;
    this.mediaRecorder = null;
    this.chunks = [];
  }
}

async function uploadBatch(blob: Blob, purpose: 'quote' | 'lookup' | 'note'): Promise<FinalResult> {
  const form = new FormData();
  form.append('audio', blob, 'recording.webm');
  form.append('purpose', purpose);
  const resp = await api<{
    session: {
      id: string;
      transcript: string | null;
      transcriptConfidence: number | null;
      durationMs: number | null;
    };
  }>(BATCH_URL, { method: 'POST', body: form });
  return {
    sessionId: resp.session.id,
    transcript: resp.session.transcript ?? '',
    confidence: resp.session.transcriptConfidence ?? 0,
    durationMs: resp.session.durationMs ?? 0,
  };
}

function pickMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) return c;
  }
  return 'audio/webm';
}
